import { GlobeHemisphereWest } from '@phosphor-icons/react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  appZone,
  deviceZone,
  displayZoneAck,
  displayZoneOverride,
  setDisplayZone,
  zoneAbbr,
  zoneCity,
  zoneSwitchPrompt,
  zonesDiffer,
} from '@/core/time';
import { useWorldVersion } from '@/modules/activity/trace';
import { useTripsVersion } from '@/modules/connections/trips';
import { Button, useClockKey } from '@/ui/components';
import { sensedZone } from './where';
import '@/ui/zone.css';

// The app follows this device's zone. When Trace or Trips App says you're somewhere whose clock
// differs (a laptop left on home time, a phone that hasn't caught up after landing), ask once
// whether to switch. Mounted once by the app shell; the device-change prompt goes first.

const ACK_KEY = 'circa.zoneSenseAck';

function readAck(): string | null {
  try {
    return globalThis.localStorage?.getItem(ACK_KEY) ?? null;
  } catch {
    return null;
  }
}

export function ZoneSensePrompt() {
  useClockKey();
  useWorldVersion();
  useTripsVersion();
  const [ack, setAck] = useState(readAck);
  const now = Date.now();
  if (zoneSwitchPrompt(displayZoneOverride(), deviceZone(), displayZoneAck())) return null;
  const s = sensedZone(now);
  const cur = appZone();
  if (!s || s.tz === ack || !zonesDiffer(s.tz, cur, now)) return null;
  const keep = () => {
    try {
      globalThis.localStorage?.setItem(ACK_KEY, s.tz);
    } catch {}
    setAck(s.tz);
  };
  const to = zoneCity(s.tz);
  const abbr = zoneAbbr(s.tz, now);
  return createPortal(
    <div className="zone-prompt" role="status">
      <GlobeHemisphereWest size={18} className="zone-prompt__icon" />
      <div className="zone-prompt__text">
        {s.from} has you in {to}
        {abbr ? ` (${abbr})` : ''}, but the app is on {zoneCity(cur)} time. Switch to {to}?
      </div>
      <div className="zone-prompt__actions">
        <Button
          size="sm"
          variant="primary"
          onClick={() => setDisplayZone(s.tz === deviceZone() ? null : s.tz)}
        >
          Switch
        </Button>
        <Button size="sm" variant="ghost" onClick={keep}>
          Keep {zoneCity(cur)}
        </Button>
      </div>
    </div>,
    document.body,
  );
}
